import { useEffect, useState } from 'react';
import posthog from './posthog';

/**
 * Feature flags, read from PostHog, with two local overrides for development
 * and QA (both win over PostHog):
 *   - `?<flag>=1` / `?<flag>=0` in the URL, remembered for the tab session
 *   - `VITE_FLAG_<FLAG>=1` at build time, e.g. VITE_FLAG_ALMA_TWO_PHASE=1
 *
 * Each flag has a default (`DEFAULTS`, off unless listed). The default applies
 * when PostHog isn't configured — the mobile builds don't ship a PostHog key,
 * so they always get the default, immediately — and when PostHog hasn't
 * answered within FLAG_TIMEOUT_MS, so a slow network never holds a page
 * hostage. When PostHog does answer, a flag it defines wins, so a default-on
 * flag can still be switched off remotely.
 */
const FLAG_TIMEOUT_MS = 1500;

/** Flags that ship on. Everything else defaults to off. */
const DEFAULTS: Record<string, boolean> = {
  alma_two_phase: true,
};

const posthogConfigured = Boolean(import.meta.env.VITE_POSTHOG_KEY && import.meta.env.VITE_POSTHOG_HOST);

function override(flag: string): boolean | null {
  try {
    const param = new URLSearchParams(window.location.search).get(flag);
    if (param === '1' || param === '0') sessionStorage.setItem(`flag:${flag}`, param);
    const stored = sessionStorage.getItem(`flag:${flag}`);
    if (stored === '1' || stored === '0') return stored === '1';
  } catch { /* storage blocked — fall through */ }
  const env = (import.meta.env as Record<string, string | undefined>)[`VITE_FLAG_${flag.toUpperCase()}`];
  if (env === '1' || env === '0') return env === '1';
  return null;
}

/** `undefined` while PostHog is still deciding; then true/false. */
export function useFeatureFlag(flag: string): boolean | undefined {
  const fallback = DEFAULTS[flag] ?? false;
  const [value, setValue] = useState<boolean | undefined>(
    () => override(flag) ?? (posthogConfigured ? undefined : fallback),
  );

  useEffect(() => {
    if (value !== undefined) return;
    let settled = false;
    const settle = (v: boolean) => {
      if (settled) return;
      settled = true;
      setValue(v);
    };
    const timer = window.setTimeout(() => settle(fallback), FLAG_TIMEOUT_MS);
    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = posthog.onFeatureFlags(() => {
        // undefined = PostHog doesn't define this flag: keep the default.
        settle(posthog.isFeatureEnabled(flag) ?? fallback);
      });
    } catch {
      settle(fallback);
    }
    return () => {
      window.clearTimeout(timer);
      unsubscribe?.();
    };
  }, [flag, value, fallback]);

  return value;
}
